import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PlusIcon from "@src/assets/svg/plus-dark-icon.svg";
import { ButtonWithIcon } from "@src/components/atoms/buttons/button-with-icon/button-with-icon";
import { FlexColumnContainer } from "@src/components/templates/layouts/flex-column-container/flex-column-container";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { useI18N } from "@src/language/language-context";
import { RenderIfUserHasPermission } from "@src/components/molecules/render-if-user-has-permission/render-if-user-has-permission";
import MachineGroupsListAdminPanel from "@src/components/organisms/machine-groups-list-admin-panel/machine-groups-list-admin-panel";
import { AddMachineGroupsAdminPanelModal } from "@src/components/organisms/modals/add-machine-groups-admin-panel-modal/add-machine-groups-admin-panel-modal";
import { RootState } from "@src/reducers/store";
import {
  CrossNameSpaceOrganziation,
  MachineGroup,
} from "@src/types/namespace-configuration";
import { updateShowAddMachineGroupsAdminPanelModal } from "@src/reducers/your-reducer";
import { useMachineGroupsListAdminPanelHook } from "@src/components/organisms/machine-groups-list-admin-panel/use-machine-groups-list-admin-panel-hook";
import styles from "./machine-groups-view-admin-panel.module.scss";

const cssClassName = "machine-groups-view-admin-panel";

interface MachineGroupsViewAdminPanelProps {
  isLoadingOrganizations: boolean;
  organizations: CrossNameSpaceOrganziation[];
}

const MachineGroupsViewAdminPanel = ({
  isLoadingOrganizations,
  organizations,
}: MachineGroupsViewAdminPanelProps) => {
  const i18n = useI18N();
  const dispatch = useDispatch();
  const showMachineGroupModal = useSelector(
    (state: RootState) =>
      state.yourSlice.addMachineGroupsAdminPanelModal
        .showAddMachineGroupsAdminPanelModal,
  );
  const [editingMachineGroup, setEditingMachineGroup] = useState<
    MachineGroup | undefined
  >();
  const [createdMachineGroupId, setCreatedMachineGroupId] = useState<
    string | undefined
  >();
  const {
    refreshMachineGroups: reloadMachineGroups,
    ...machineGroupsListData
  } = useMachineGroupsListAdminPanelHook();
  const refreshAfterMachineGroupSave = useCallback(
    (newMachineGroupId?: string) => {
      setCreatedMachineGroupId(newMachineGroupId);
      void reloadMachineGroups();
    },
    [reloadMachineGroups],
  );
  const refreshAfterRoutingPathSave = useCallback(() => {
    void reloadMachineGroups();
  }, [reloadMachineGroups]);
  const clearPendingCreatedMachineGroupId = useCallback(
    (machineGroupId: string) => {
      setCreatedMachineGroupId((currentId) =>
        currentId === machineGroupId ? undefined : currentId,
      );
    },
    [],
  );
  const title = i18n("modules.machineGroupsViewAdminPanel.title");
  const description = i18n("modules.machineGroupsViewAdminPanel.description");
  const addGroupButton = i18n(
    "modules.machineGroupsViewAdminPanel.addGroupButton",
  );
  const handleAddGroup = () => {
    setEditingMachineGroup(undefined);
    dispatch(updateShowAddMachineGroupsAdminPanelModal(true));
  };
  const handleEditMachineGroup = (machineGroup: MachineGroup) => {
    setEditingMachineGroup(machineGroup);
    dispatch(updateShowAddMachineGroupsAdminPanelModal(true));
  };

  return (
    <FlexColumnContainer>
      <FlexRowContainer justifyContent="space-between">
        <p className={styles[`${cssClassName}__title`]}>{title}</p>
        <RenderIfUserHasPermission permission="manage-machine-groups">
          <ButtonWithIcon
            icon={PlusIcon}
            text={addGroupButton}
            fun={handleAddGroup}
          />
        </RenderIfUserHasPermission>
      </FlexRowContainer>
      <p className={styles[`${cssClassName}__description`]}>{description}</p>
      <MachineGroupsListAdminPanel
        {...machineGroupsListData}
        organizations={organizations}
        createdMachineGroupId={createdMachineGroupId}
        onCreatedMachineGroupExpanded={clearPendingCreatedMachineGroupId}
        onEditMachineGroup={handleEditMachineGroup}
        onRoutingPathSaved={refreshAfterRoutingPathSave}
      />
      <RenderIfUserHasPermission permission="manage-machine-groups">
        {showMachineGroupModal && (
          <AddMachineGroupsAdminPanelModal
            isLoadingOrganizations={isLoadingOrganizations}
            organizations={organizations}
            editingMachineGroup={editingMachineGroup}
            onMachineGroupSaved={refreshAfterMachineGroupSave}
          />
        )}
      </RenderIfUserHasPermission>
    </FlexColumnContainer>
  );
};

export default MachineGroupsViewAdminPanel;
